import { useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  clearQueryParamSilently,
  getQueryParam,
  setQueryParamSilently,
} from '@cast/utils';

import { Criterion } from '../types';
import { getInitialValue, isClient, isServer } from '../utils';

const IS_DEV = import.meta.env.DEV;

const makeCriterionStorageKey = (urlKey: string, criterionKey: string) =>
  `${urlKey}_${criterionKey}`;

const readFromCriterionStorage = <V>(
  criterion: Criterion<V>,
  urlKey?: string
): V | undefined => {
  if (!criterion.storage || !urlKey) {
    return undefined;
  }

  try {
    const storageKey = makeCriterionStorageKey(urlKey, criterion.key);
    const value = criterion.storage.getItem(storageKey);
    if (value === null) {
      return undefined;
    }
    return JSON.parse(value) as V;
  } catch {
    return undefined;
  }
};

const writeToCriterionStorage = <V>(
  criterion: Criterion<V>,
  value: V,
  urlKey?: string
): void => {
  if (!criterion.storage || !urlKey) {
    return;
  }

  const storageKey = makeCriterionStorageKey(urlKey, criterion.key);

  if (value == null) {
    criterion.storage.removeItem(storageKey);
  } else {
    criterion.storage.setItem(storageKey, JSON.stringify(value));
  }
};

const deleteCriterionFromStorage = <V>(
  criterion: Criterion<V>,
  urlKey?: string
): void => {
  writeToCriterionStorage(criterion, null, urlKey);
};

const getInitialValueWithStorage = (
  criteria: Criterion[],
  urlKey?: string
): Record<string, any> => {
  const initial: Record<string, any> = {};
  for (const criterion of criteria) {
    const { key, initialValue } = criterion;
    // Try to restore from storage first
    const storedValue = readFromCriterionStorage(criterion, urlKey);
    if (storedValue !== undefined) {
      initial[key] = storedValue;
    } else if (initialValue !== undefined && initialValue !== null) {
      initial[key] = initialValue;
    }
  }
  return initial;
};

const addToUrl = (urlKey: string, value: Record<string, any>) => {
  setQueryParamSilently(urlKey, encodeURIComponent(JSON.stringify(value)));
};

const addOrRemoveToUrl = (value: Record<string, any>, urlKey?: string) => {
  if (urlKey) {
    if (Object.keys(value).length) {
      addToUrl(urlKey, value);
    } else {
      clearQueryParamSilently(urlKey);
    }
  }
};

const decodeCriteriaFromUrl = (id: string): Record<string, string> => {
  const searchParams = getQueryParam(id);
  if (!searchParams) {
    return {};
  }
  try {
    return JSON.parse(decodeURIComponent(searchParams));
  } catch (e) {
    console.error(e);
    return {};
  }
};

const criteriaToValues = (
  criteria: Criterion[],
  values: Record<string, any>
) => {
  const result: Record<string, any> = {};
  for (const { key } of criteria) {
    if (key in values) {
      result[key] = values[key];
    }
  }
  return result;
};

const useValueSubset = (
  criteria: Criterion[],
  values: Record<string, any>
): Record<string, any> => {
  return useMemo(() => criteriaToValues(criteria, values), [criteria, values]);
};

export const useCriteria = (allCriteria: Criterion[], urlKey?: string) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValues = useMemo(() => getInitialValue(allCriteria), []);

  const [selectedCriteria, setSelectedCriteria] = useState<Record<string, any>>(
    () => {
      if (urlKey) {
        // Priority: URL values > storage values > initial values
        return Object.assign(
          {},
          initialValues,
          // TODO (Justas): Instead of `urlKey` and others, we could have a single `id` and remove the other 'keys' as this granularity doesn't bring much value, but increases complexity
          getInitialValueWithStorage(allCriteria, urlKey),
          decodeCriteriaFromUrl(urlKey) || {}
        );
      }
      return Object.assign({}, initialValues);
    }
  );

  const clientCriteria = useMemo(
    () => allCriteria.filter(isClient),
    [allCriteria]
  );
  const clientValues = useValueSubset(clientCriteria, selectedCriteria);

  const serverCriteria = useMemo(
    () => allCriteria.filter(isServer),
    [allCriteria]
  );
  const serverValues = useValueSubset(serverCriteria, selectedCriteria);

  const getCriterion = (key: string) => allCriteria.find((c) => c.key === key);
  const setValue = (key: string, value: any) => {
    const criterion = getCriterion(key);
    if (IS_DEV) {
      if (!criterion) {
        throw new Error(`Trying to set non existing criterion: ${key}`);
      }
    }
    setSelectedCriteria((currentValue) => {
      if (isEqual(currentValue[key], value)) {
        return currentValue;
      }
      const newValue = { ...currentValue };
      if (value == null) {
        delete newValue[key];
      } else {
        newValue[key] = value;
      }
      addOrRemoveToUrl(newValue, urlKey);

      // Persist to storage if criterion has storage configured
      if (criterion) {
        writeToCriterionStorage(criterion, value, urlKey);
      }

      return newValue;
    });
  };

  const clearValue = (keyToRemove?: string | string[]) => {
    const removedValues: Record<string, any> = {};
    setSelectedCriteria((currentValue) => {
      const newValue: Record<string, any> = {};
      if (keyToRemove) {
        const keysToRemove = Array.isArray(keyToRemove)
          ? keyToRemove
          : [keyToRemove];
        let removed = false;
        for (const key in currentValue) {
          if (!keysToRemove.includes(key)) {
            newValue[key] = currentValue[key];
          } else {
            removedValues[key] = currentValue[key];
            removed = true;
            // Clear from storage if criterion has storage configured
            const criterion = getCriterion(key);
            if (criterion) {
              deleteCriterionFromStorage(criterion, urlKey);
            }
          }
        }
        if (removed) {
          addOrRemoveToUrl(newValue, urlKey);
          return newValue;
        }
        return currentValue;
      }
      // Clear all - also clear storage for criteria with storage configured
      for (const criterion of allCriteria) {
        if (criterion.storage) {
          deleteCriterionFromStorage(criterion, urlKey);
        }
      }
      return newValue;
    });

    return removedValues;
  };

  const resetInitial = () => {
    // Clear storage for criteria with storage configured
    for (const criterion of allCriteria) {
      if (criterion.storage) {
        deleteCriterionFromStorage(criterion, urlKey);
      }
    }
    setSelectedCriteria(initialValues);
    addOrRemoveToUrl(initialValues, urlKey);
  };

  const valuesChanged = useMemo(() => {
    return !isEqual(initialValues, selectedCriteria);
  }, [initialValues, selectedCriteria]);

  const setValues = (values: Record<string, any>) => {
    // Update storage for criteria with storage configured
    for (const criterion of allCriteria) {
      if (criterion.storage) {
        const value = values[criterion.key];
        writeToCriterionStorage(criterion, value, urlKey);
      }
    }
    setSelectedCriteria(values);
    addOrRemoveToUrl(values, urlKey);
  };

  return {
    values: selectedCriteria,
    setValue,
    setValues,
    clearValue,
    resetInitial,
    valuesChanged,
    clientValues,
    clientCriteria,
    serverValues,
    serverCriteria,
  };
};
